<template>
    <div>
        <div style="display: flex; flex: 1; justify-content: flex-end;">
            <v-checkbox
                v-model="showDateWithEmptyData"
                color="secondary lighten-1"
                label="Mostrar datas com dados ausentes"
                @change="applyDateWithEmptyData"
            ></v-checkbox>
        </div>

        <v-data-table
            id="data-table-monthly-data"
            :headers="headers"
            :items="items"
            class="primary darken-1 white--text"
            hide-default-footer
            :page.sync="page"
            :items-per-page="itemsPerPage"
            @page-count="pageCount = $event"
        >
            <template v-slot:[`item.date`]="{ item }">
                <v-timeline class="timeline-table" dense>
                    <v-timeline-item
                        color="primary lighten-1"
                        small
                        fill-dot
                    > {{ item.date }}</v-timeline-item>
                </v-timeline>
            </template>

            <template v-for="(sensorKey, index) in sensorColumnKeys" v-slot:[`item.sensor${sensorKey}Value`]="{ item }">
                <daily-monthly-sensor-value-table
                    :index="index"
                    :item="item"
                    :highlightsCards="verifyHighlights(highlightsCards)"
                    :sensorKey="sensorKey"
                    :type="searchResult.type"
                    :key="index"
                >
                </daily-monthly-sensor-value-table>
            </template>

        </v-data-table>
        <div class="mt-3" style="display: flex; align-items: center;justify-content: space-between;font-style: normal;font-weight: normal;font-size: 12px;line-height: 16px;">
            <div>Mostrando {{startItem}}-{{endItem}} de {{items.length}} resultados</div>
            <div style="display: flex; align-items: center;">
                <span class="mr-3">Linhas por página:</span>
                <div style="max-width: 64px;">
                    <v-select
                        v-model="itemsPerPage"
                        :items="itemsPerPageOptions"
                        dense
                        hide-details
                        item-color="secondary lighten-1"
                        color="secondary lighten-1"
                    ></v-select>
                </div>
                <v-pagination
                    class="ml-3 pagination-data elevation-0"
                    color="secondary lighten-1"
                    v-model="page"
                    :length="pageCount"
                ></v-pagination>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import { jsPDF } from "jspdf"
// eslint-disable-next-line no-unused-vars
import { ExportToCsv } from "export-to-csv";
import writeXlsxFile from 'write-excel-file'
import html2canvas from "html2canvas";
import DailyMonthlySensorValueTable from "./DailyMonthlySensorValueTable.vue";
export default {
    name: "SummaryTableData",
    props: {
        searchResult:{
            type: Object,
        },
        highlightsCards:{
            type: Array,
        }
    },
    components:{
        DailyMonthlySensorValueTable,
    },
    data:()=>({
        showDateWithEmptyData: true,
        itemsPerPage: undefined,
        itemsPerPageOptions: [],
        page: 1,
        pageCount: undefined,
        headers: [],
        items:[],
        rawItems: [],
        sensorColumnKeys: [],
        exportFileName: "export"
    }),

    created(){
        this.initialize();
    },

    watch:{
        itemsPerPage(value){
            if(value){
                this.page = 1;
            }
        },

        searchResult(value){
            if(value){
                this.initialize();
            }
        }
    },

    computed:{
        startItem(){
             return (this.page - 1) * this.itemsPerPage + 1;
        },

        endItem(){
             return this.page * this.itemsPerPage;
        }
    },

    methods:{
        initialize(){
            const { sensors, type, resultList } = this.searchResult;
            this.sensorColumnKeys = sensors.map((sensor) => sensor.codigo);
            this.headers = this.generateHeaders(sensors, type);
            let generatedItems = [];
            if(type === "mensal") generatedItems = this.generateMonthlyItems(resultList);
            if(type === "diario") generatedItems = this.generateDailyItems(resultList);
            this.rawItems = this.groupItemsByDate(generatedItems);
            this.items = Object.assign([], this.rawItems);
            this.itemsPerPageOptions = type === "mensal" ? [ 12, 25, 50, 100] : [ 10, 25, 50, 100];
            this.itemsPerPage = this.itemsPerPageOptions[0];
            this.exportFileName = this.generateExportFileName(this.searchResult);
        },

        generateHeaders(sensors, type){
            const headers = []
            const dateHeader = {
                text: type === "diario" ? "Dia" : type === "mensal" ? "Mês" : " Hora",
                align: 'start',
                sortable: true,
                value: 'date',
                width: 300,
            };
            headers.push(dateHeader);
            for (const sensor of sensors) {
                const { codigo, descricao } = sensor;
                headers.push({
                    text: descricao,
                    align: 'end',
                    sortable: true,
                    value: `sensor${codigo}Value`,
                    width: 300,
                });
            }
            return headers;
        },

        generateItemsPerPageOptions(){
            const itemsLength = this.items.length;
            const factor = Math.trunc(itemsLength / 5);
            const finalFactor = (5*factor) < itemsLength ? factor + 1 : factor;
            return Array(finalFactor).fill().map((_, index) => (index + 1) * 5);
        },

        generateMonthlyItems(resultList){
            return resultList.map((result) => ({
                date: this.capitalize(moment(`${result.ano}-${result.mes}`, "YYYY-MM").locale("pt-br").format("MMMM/YYYY")),
                dateIso: `${result.ano}-${result.mes}`,
                value: result.valor,
                sensorId: result.sensorId,
                tag: result.tag,
            }));
        },

        generateDailyItems(resultList){
            return resultList.map((result) => ({
                date: moment(result.data, "YYYY-MM-DD").format("DD/MM/YYYY"),
                dateIso: result.data,
                value: result.valor,
                sensorId: result.sensorId,
                tag: result.tag,
            }));
        },

        groupItemsByDate(items){
            const groupItems = [];
            const dateKeys = [];
            for (const item of items) {
                const { date } = item;
                if(dateKeys.indexOf(date) === -1) dateKeys.push(date);
            }
            for (const dateKey of dateKeys) {
                let groupItem = { date: dateKey };
                const resultsByDate = items.filter((item) => item.date === dateKey);
                for (const result of resultsByDate) {
                    const { value, sensorId, dateIso } = result;
                    groupItem.dateIso = dateIso;
                    groupItem[`sensor${sensorId}Value`] = value;
                    groupItem[`sensor${sensorId}Id`] = sensorId;
                }
                groupItems.push(groupItem);
            }
            return groupItems;
        },

        pagesTotal(){
            const itemsLength = this.items.length;
            if(itemsLength <= this.itemsPerPage) return 1;
            return Math.trunc(itemsLength/this.itemsPerPage) + 1
        },

        verifyHighlights(highlights){
            if(!highlights) return [];
            const sensors = [];
            for (const highlight of highlights) {
                const { sensorId } = highlight;
                if(sensors.indexOf(sensorId) === -1) sensors.push(sensorId)
            }
            const highlightsVerified = [];
            for (const sensor of sensors) {
                const verified = this.changeTheSameTag(highlights.filter((item) => item.sensorId === sensor));
                highlightsVerified.push(...verified);
            }
            return highlightsVerified;
        },

        changeTheSameTag(highlights){
            const { type } = this.searchResult;
            const min = highlights.find(item => item.tag === "min");
            const max = highlights.find(item => item.tag === "max");
            if(min && max){
                const minDate = type === "mensal" ? `${min.ano}-${min.mes}` : min.data;
                const maxDate = type === "mensal" ? `${max.ano}-${max.mes}` : max.data;
                const typeDate = type === "mensal" ? "months" : "days";
                if(moment(minDate).isSame(moment(maxDate), typeDate)){
                    return [{ ...max, tag: "same"}];
                }
            }
            return highlights;
        },

        generatePDF(settings) {
            const rawItems = Object.assign([], this.items);
            this.items = this.applySettings(settings, this.rawItems);
            this.itemsPerPage = 100;
            setTimeout(()=>{
                const tableElement = document.getElementById('data-table-monthly-data');
                html2canvas(tableElement).then(canvas => {
                    var imgData = canvas.toDataURL('image/png');
                    var imgWidth = 210;
                    var pageHeight = 295;
                    var imgHeight = canvas.height * imgWidth / canvas.width;
                    var heightLeft = imgHeight;
                    var doc = new jsPDF('p', 'mm');
                    var position = 0;
                    doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                    heightLeft -= pageHeight;
                    while (heightLeft >= 0) {
                        position = heightLeft - imgHeight;
                        doc.addPage();
                        doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                        heightLeft -= pageHeight;
                    }
                    doc.save(`${this.exportFileName}.pdf`);
                });
                this.itemsPerPage = this.itemsPerPageOptions[0];
                this.items = rawItems;
            }, 1000);
        },

        generateCSV(settings){
            const rawItems = Object.assign([], this.items);
            this.items = this.applySettings(settings, this.items);
            let csvContent = "data:text/csv;charset=utf-8,";
            const mapItems = this.mapItemsToExport(this.items);
            /* const options = {
                fieldSeparator: ';',
                quoteStrings: '"',
                decimalSeparator: '.',
                showLabels: true,
                showTitle: false,
                useTextFile: false,
                useBom: true,
                useKeysAsHeaders: true,
                fileName: "tste"
            };

            const csvExporter = new ExportToCsv(options);

            csvExporter.generateCsv(mapItems) */

            csvContent += [
                Object.keys(mapItems[0]).join(";"),
                ...mapItems.map(item => Object.values(item).join(";"))
            ]
                .join("\n")
                .replace(/(^\[)|(\]$)/gm, "");

            const data = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", data);
            link.setAttribute("download", `${this.exportFileName}.csv`);
            link.click();
            this.items = rawItems;
        },

        async generateXLS(settings){
            const rawItems = Object.assign([], this.items);
            this.items = this.applySettings(settings, this.items);
            const mapItems = this.mapItemsToExport(this.items);
            const columns = Object.keys(mapItems[0]);
            const schema = columns.map((column) => ({ column, type: String, fontStyle: "normal", value: item => item[column] }));
            await writeXlsxFile(mapItems, {
                schema,
                fileName: `${this.exportFileName}.xlsx`,
                headerStyle: {
                    fontWeight: 'normal',
                    align: 'center'
                },
            });
            this.items = rawItems;
        },

        mapItemsToExport(items){
            const { sensors } = this.searchResult;
            return items.map((item) => {
                const { dateIso } = item;
                const sensorsValues = {};
                for (const sensorKey of this.sensorColumnKeys) {
                    const sensorFind = sensors.find(sensor => sensor.codigo == sensorKey);
                    sensorsValues[sensorFind.descricao] = item[`sensor${sensorKey}Value`] !== null ? Number(item[`sensor${sensorKey}Value`]) : "";
                }
                return {date: dateIso,  ...sensorsValues}
            });
        },

        applySettings(settings, items){
            const {option, replaceData} = settings;
            if(option === "replace-data" && replaceData){
                return this.replaceEmptyValue(items, replaceData);
            }
            if(option === "remove-dates-with-empty-values"){
                return this.removeDatesWithEmptyValues(items);
            }
            return items;
        },

        replaceEmptyValue(items, replaceData){
            const itemsReplaced = [];
            for (const item of items) {
                const verifyItem = Object.assign({}, item);
                const keys = Object.keys(verifyItem);
                for (const key of keys) {
                    if(key === "date") continue;
                    if(verifyItem[key] !== 0 && !verifyItem[key]){
                        verifyItem[key] = replaceData;
                    }
                }
                itemsReplaced.push(verifyItem);
            }
            return itemsReplaced;
        },

        removeDatesWithEmptyValues(items){
            const itemsReplaced = [];
            for (const item of items) {
                const verifyItem = Object.assign({}, item);
                const verifyItemAsArray = Object.entries(verifyItem);
                const emptyDates = verifyItemAsArray.filter((entryItem) => {
                    const [ key, value ] = entryItem;
                    return key.includes("Value") && (value || value === 0)
                });
                if(emptyDates.length){
                    itemsReplaced.push(verifyItem);
                }
            }
            return itemsReplaced;
        },

        applyDateWithEmptyData(show){
            if(show){
                this.items = Object.assign([], this.rawItems);
            }
            else{
                this.items = this.removeDatesWithEmptyValues(Object.assign([], this.rawItems));
            }
        },

        formatDate(date, type){
            const stringFormat = type === "mensal" ? "MMMM/YYYY" : type === "diario" ? "DD/MM/YYYY" : "DD/MM/YYYY HH:mm";
            const dateFormatted = moment(date).tz("America/Sao_Paulo").locale("pt-br").format(stringFormat);
            return type === "mensal" ? this.capitalize(dateFormatted) : dateFormatted;
        },

        capitalize(text){
            return text[0].toUpperCase() + text.slice(1);
        },

        generateExportFileName(searchResult){
            const typeName = searchResult.type === "mensal" ? "sumario_mensal" : "sumario_diario";
            const stationName = searchResult.station.nome;
            const periodFormat = `${this.formatDate(searchResult.date_start, searchResult.type)} até ${this.formatDate(searchResult.date_end, searchResult.type)}`
            return `${stationName}-${typeName}-${periodFormat}`;
        }
    }
}
</script>

<style scoped>

    #data-table-monthly-data ::v-deep th{
        border-bottom: 1px solid #D9D9D9 !important;
        padding: 0px 20px !important;
        color: #FFFFFF;
        font-style: normal;
        font-weight: 500;
        font-size: 14px;
        line-height: 18px;
    }

    #data-table-monthly-data ::v-deep td{
        border-bottom: 1px solid #2D324F !important;
        padding: 0px 20px !important;
        font-style: normal;
        font-weight: 500;
        font-size: 14px !important;
        line-height: 18px;
    }

     #data-table-monthly-data ::v-deep tr:hover{
        background: var(--v-primary-darken2);
    }

    #data-table-monthly-data ::v-deep .v-data-table__wrapper{
        border-bottom: 1px solid #2D324F !important;
    }

    .timeline-table::before{
        background: #2D324F !important;
    }

    .pagination-data ::v-deep .v-pagination__navigation{
        background: var(--v-primary-darken1) !important;
    }

    .pagination-data ::v-deep .v-pagination__item.v-pagination__item--active{
        background: var(--v-secondary-lighten1) !important;
    }

    .pagination-data ::v-deep .v-pagination__item{
        background: var(--v-primary-darken1) !important;
    }

</style>
